import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Col,
    Container,
    Row
} from 'reactstrap';

import { UserList } from './UserList';

import { ErrorMsgSeverity } from '../../ErrorBanner/ErrorBanner';

import {
    DetailedUserResponse,
    RoleResponse,
    TenantUserMetadataSetting,
    PortalTextCommon,
    PortalTextSettings,
} from '../../../Models/Api/strongbox.financialportal';

import { InlineStylingValues } from '../../../Utils/Constants';

import { DeleteUser, ReinviteUser, UpdateUser } from '../../../Services/UserService';

import { ApplicationState } from '../../../Store';
import {
    BrandConfig,
    GetBrandConfig,
    TenantActionCreators,
    GetUserMetadataSettings,
} from '../../../Store/Tenant';
import { GetPortalText } from '../../../Store/PortalAssets';
import { actionCreators as UIStateActionCreators } from '../../../Store/UIState';
import { GetDetailedUsers, GetLoggedInUser, GetLoggedInUserExternalId, LoadAllDetailedUsers, LoadingUsers } from '../../../Store/User';
import { actionCreators as UserRolesActionCreators, GetRoles, GetRolesLoading, LoadUserRoles } from '../../../Store/UserRoles';

import { ErrorBanner } from '../../ErrorBanner/ErrorBanner';

import { LoadingIndicator } from '../../LoadingIndicator/LoadingIndicator';

import '../../Main.scss';

type InjectedReduxState = {
    users: DetailedUserResponse[];
    brandConfig: BrandConfig;
    userRoles: RoleResponse[];
    loadingUserRoles: boolean;
    loadingUsers: boolean;
    loggedInUserExternalId: string;
    loggedInUserId: string;
    tenantMetadataSettings: TenantUserMetadataSetting[];
    commonText?: PortalTextCommon;
    settingsText?: PortalTextSettings;
};

type InjectedActionCreators = typeof UIStateActionCreators & typeof UserRolesActionCreators & typeof TenantActionCreators;

type UserManagementProps = {
}

type Props = UserManagementProps & InjectedActionCreators & InjectedReduxState;

type UserMsg = {
    msg?: string;
    severity?: ErrorMsgSeverity;
}

const UserManagementComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        brandConfig,
        GetEmailDomains,
        GetUserMetadataSettings,
        loadingUsers,
        loadingUserRoles,
        loggedInUserId,
        loggedInUserExternalId,
        userRoles,
        users,
        tenantMetadataSettings,
        commonText,
        settingsText,
    } = props;

    const [deletingUser, setDeletingUser] = React.useState<boolean>(false);
    const [updatingUser, setUpdatingUser] = React.useState<boolean>(false);
    const [reinvitingUser, setReinvitingUser] = React.useState<boolean>(false);
    const [userMsg, setUserMsg] = React.useState<UserMsg>({});

    const loadingIndicator = (
        <LoadingIndicator
            overlayStyle={{
                position: 'absolute',
                left: '49%',
                top: '35%'
            }}
            active={true}
        />
    );

    const loading = (): boolean => {
        return loadingUserRoles || loadingUsers;
    }

    const working = (): boolean => {
        return deletingUser || updatingUser || reinvitingUser;
    }

    const loadRequiredResources = (): void => {
        // TODO: Trigger reload of users and roles.
        LoadAllDetailedUsers();
        LoadUserRoles(() => { });
        GetEmailDomains();
        GetUserMetadataSettings();
    }

    React.useEffect(() => {
        loadRequiredResources();

        // I want this to take effect on mount and loadRequiredResources won't change
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    const deleteUser = (user: DetailedUserResponse, onComplete?: (errorMsg?: string) => void): void => {
        setDeletingUser(true);
        DeleteUser(user.id)
            .then(success => {
                setDeletingUser(false);
                if (success) {
                    loadRequiredResources();
                    onComplete && onComplete();
                } else {
                    const errorMsg = settingsText?.errorDeletingUser || '';
                    if (!!onComplete) {
                        onComplete(errorMsg);
                    } else {
                        setUserMsg({ msg: errorMsg });
                    }
                }
            });
    }

    const updateUser = (user: DetailedUserResponse): void => {
        setUpdatingUser(true);
        UpdateUser(user, user.id !== loggedInUserId)
            .then(success => {
                setUpdatingUser(false);
                if (success) {
                    loadRequiredResources();
                } else {
                    setUserMsg({ msg: settingsText?.errorSavingUser || '' });
                }
            });
    }

    const reinviteUser = async (user: DetailedUserResponse, onComplete: (errorMsg?: string) => void): Promise<void> => {
        setReinvitingUser(true);
        ReinviteUser(user.id)
            .then(success => {
                setReinvitingUser(false);
                if (!success) {
                    onComplete(settingsText?.errorReinvitingUser || '')
                } else {
                    onComplete();
                }
            });
    }

    return (
        <Container fluid>
            {
                userMsg.msg && (
                    <div style={{ paddingLeft: '15px', paddingTop: '25px' }}>
                        <ErrorBanner
                            errorState={{
                                summaryMessage: userMsg.msg,
                                severity: userMsg.severity,
                            }}
                            onDefaultActionButton={() => {
                                setUserMsg({});
                            }}
                            commonText={commonText}
                        />
                    </div>
                )
            }
            {
                working() && loadingIndicator
            }
            {!loading() && (
                <Row style={{ marginBottom: InlineStylingValues.BasicSectionSeparation}} >
                    <Col className={'data-container  data-container-primary'}>
                        <UserList
                            availableRoles={userRoles}
                            users={users}
                            brandConfig={brandConfig}
                            loggedInUserExternalId={loggedInUserExternalId}
                            onUpdateUser={user => updateUser(user)}
                            onDeleteUser={(user, onComplete) => deleteUser(user, onComplete)}
                            onReinviteUser={(user, onComplete) => reinviteUser(user, onComplete)}
                            tenantUserMetadataSettings={tenantMetadataSettings}
                            commonText={commonText}
                            settingsText={settingsText}
                        />
                    </Col>
                </Row>
            )}
            {loading() && loadingIndicator}
        </Container>
    );
}

export const UserManagement = connect<InjectedReduxState, InjectedActionCreators, UserManagementProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            users: GetDetailedUsers(appState),
            brandConfig: GetBrandConfig(appState),
            userRoles: GetRoles(appState),
            loadingUserRoles: GetRolesLoading(appState),
            loadingUsers: LoadingUsers(appState),
            loggedInUserExternalId: GetLoggedInUserExternalId(appState),
            loggedInUserId: GetLoggedInUser(appState)?.id || '',
            tenantMetadataSettings: GetUserMetadataSettings(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            settingsText: GetPortalText<PortalTextSettings>(appState, 'settings'),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...UIStateActionCreators,
            ...UserRolesActionCreators,
            ...TenantActionCreators,
        },
        dispatch
    )
)(UserManagementComponent);
